import {
  CRM_MODULE_COLLECTION_KEYS,
  CRM_MODULE_COLLECTIONS as CRM_MODULE_COLLECTION_KINDS,
  CRM_MODULE_PRESET_KEYS,
  CRM_MODULE_PRESETS as CRM_MODULE_PRESET_MODULES,
  CRM_MODULES,
  DASHBOARD_PATH,
  buildDefaultModuleSettings as buildManifestDefaultModuleSettings,
  buildPresetModuleSettings as buildManifestPresetModuleSettings,
  getDefaultLandingPath,
  getDefaultRoleDefaults as getManifestDefaultRoleDefaults,
  normalizeRoleDefaults as normalizeManifestRoleDefaults,
  resolveModuleSettingsDependencies as resolveManifestDependencies,
  type CrmModuleCollectionKind,
} from '@topolo-crm/modules';
import type {
  CrmModuleCollectionKey,
  CrmModuleKey,
  CrmModulePresetKey,
  OrgRoleDefault,
  OrgModuleSettings,
} from '../types/api';
import { CRM_COPY } from './crm-copy';

const SETTINGS_COPY = CRM_COPY.settingsPage;

export interface CrmModuleCollectionDefinition {
  key: CrmModuleCollectionKey;
  label: string;
  description: string;
  kind: CrmModuleCollectionKind;
}

export interface CrmModuleDefinition {
  key: CrmModuleKey;
  label: string;
  description: string;
  path: string;
  collectionKey: CrmModuleCollectionKey;
  dependencies: CrmModuleKey[];
}

export interface CrmModulePresetDefinition {
  key: CrmModulePresetKey;
  label: string;
  description: string;
  modules: CrmModuleKey[];
}

// These lists are built when the module is imported, before a translator is bound, so their text is read
// through getters at the moment it is used rather than captured here.
export const CRM_MODULE_DEFINITIONS: CrmModuleDefinition[] = CRM_MODULES.map(module => ({
  key: module.key,
  get label() {
    return SETTINGS_COPY.moduleDefinitions[module.key].label;
  },
  get description() {
    return SETTINGS_COPY.moduleDefinitions[module.key].description;
  },
  path: module.path,
  collectionKey: module.collection,
  dependencies: module.dependencies,
}));

export const CRM_MODULE_COLLECTIONS: CrmModuleCollectionDefinition[] =
  CRM_MODULE_COLLECTION_KEYS.map(key => ({
    key,
    get label() {
      return SETTINGS_COPY.collections[key].label;
    },
    get description() {
      return SETTINGS_COPY.collections[key].description;
    },
    kind: CRM_MODULE_COLLECTION_KINDS[key].kind,
  }));

export const CRM_MODULE_PRESETS: CrmModulePresetDefinition[] = CRM_MODULE_PRESET_KEYS.map(key => ({
  key,
  get label() {
    return SETTINGS_COPY.presets[key].label;
  },
  get description() {
    return SETTINGS_COPY.presets[key].description;
  },
  modules: CRM_MODULE_PRESET_MODULES[key],
}));

const MODULE_DEFINITION_MAP = new Map(CRM_MODULE_DEFINITIONS.map(module => [module.key, module]));

export const buildDefaultModuleSettings = buildManifestDefaultModuleSettings;

export const DEFAULT_MODULE_SETTINGS: OrgModuleSettings = buildDefaultModuleSettings();

export const buildPresetModuleSettings = buildManifestPresetModuleSettings;

export const resolveModuleSettingsDependencies = resolveManifestDependencies;

export function getModuleDefinition(moduleKey: CrmModuleKey): CrmModuleDefinition {
  return MODULE_DEFINITION_MAP.get(moduleKey) as CrmModuleDefinition;
}

export function getModuleLabel(moduleKey: CrmModuleKey): string {
  return MODULE_DEFINITION_MAP.get(moduleKey)?.label || moduleKey;
}

export function getModuleCollectionModules(
  collectionKey: CrmModuleCollectionKey
): CrmModuleDefinition[] {
  return CRM_MODULE_DEFINITIONS.filter(module => module.collectionKey === collectionKey);
}

export function getModuleDependencies(moduleKey: CrmModuleKey): CrmModuleKey[] {
  return MODULE_DEFINITION_MAP.get(moduleKey)?.dependencies || [];
}

export function getDependentModules(moduleKey: CrmModuleKey): CrmModuleKey[] {
  return CRM_MODULE_DEFINITIONS.filter(module => module.dependencies.includes(moduleKey)).map(
    module => module.key
  );
}

export function updateModuleSetting(
  moduleSettings: OrgModuleSettings,
  moduleKey: CrmModuleKey,
  nextValue: boolean
): OrgModuleSettings {
  const nextSettings = { ...moduleSettings, [moduleKey]: nextValue };

  if (!nextValue) {
    const queue = [moduleKey];
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) break;
      for (const dependent of getDependentModules(current)) {
        if (nextSettings[dependent]) {
          nextSettings[dependent] = false;
          queue.push(dependent);
        }
      }
    }
  }

  return resolveModuleSettingsDependencies(nextSettings);
}

function resolveSettings(moduleSettings?: OrgModuleSettings | null): OrgModuleSettings {
  return resolveModuleSettingsDependencies(moduleSettings || buildDefaultModuleSettings());
}

export function getDefaultRoleDefaults(
  moduleSettings?: OrgModuleSettings | null
): OrgRoleDefault[] {
  return getManifestDefaultRoleDefaults(resolveSettings(moduleSettings));
}

export function normalizeRoleDefaults(
  moduleSettings: OrgModuleSettings,
  roleDefaults?: OrgRoleDefault[] | null
): OrgRoleDefault[] {
  return normalizeManifestRoleDefaults(resolveSettings(moduleSettings), roleDefaults);
}

export function getLandingPathOptions(
  moduleSettings?: OrgModuleSettings | null
): Array<{ value: string; label: string }> {
  const settings = resolveSettings(moduleSettings);
  return [
    { value: DASHBOARD_PATH, label: SETTINGS_COPY.landingPathOptions['/dashboard'] },
    ...CRM_MODULE_DEFINITIONS.filter(module => settings[module.key]).map(module => ({
      value: module.path,
      label: module.label,
    })),
  ];
}

export function getDefaultModulePath(
  moduleSettings?: OrgModuleSettings | null,
  roleDefaults?: OrgRoleDefault[] | null,
  role?: string | null
): string {
  return getDefaultLandingPath(resolveSettings(moduleSettings), roleDefaults, role);
}
